package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import com.routexk.quiz.di.LocalAppContainer
import com.routexk.quiz.funnel.FunnelApi
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.ui.FunnelCta
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.routexk.quiz.ui.theme.toRgbString
import com.routexk.quiz.ui.utils.isMobile
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Page("/")
@Composable
fun HomePage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()
    val mobile = isMobile()

    LaunchedEffect(Unit) {
        Ga4.screenView("landing", 1)
        FbPixel.pageView()
        FbPixel.track("ViewContent", params = arrayOf("content_name" to "landing"))
        FunnelApi.ensureSession(container.defaultHttpClient)
        FunnelApi.trackEvent(container.defaultHttpClient, "funnel_landing_view")
        // _fbp appears once fbevents.js loads; _fbc only when the visit came
        // from an ad click (?fbclid). Both feed CAPI match quality.
        FunnelApi.saveFbData(container.defaultHttpClient, "fbp" to FbPixel.fbp(), "fbc" to FbPixel.fbc())
    }

    FunnelLayout(
        title = com.routexk.quiz.i18n.t("RouteXK — More Routes. More Money.", "RouteXK — Más rutas. Más dinero."),
        description = com.routexk.quiz.i18n.t(
            "AI route optimization for drivers: cut extra miles and earn up to \$15,000 more per year",
            "Optimización de rutas con IA para conductores: recorta millas de más y gana hasta \$15,000 más al año",
        ),
        warm = true,
        maxContentWidth = 1220.rpx,
        fillViewport = true,
        // Mobile: warm ball matches the Figma frame (top -87), glow centered behind the cards
        patternTop = if (mobile) (-87).rpx else 120.rpx
    ) {
        Column(
            Modifier.fillMaxWidth().flexGrow(1),
            verticalArrangement = Arrangement.SpaceBetween,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Hero fills the free space above the CTA bar; on desktop it is
            // vertically centered, on mobile it flows from the top with the
            // design's fixed gaps (header ->64-> cards ->41-> headline ->16-> pill)
            Box(
                Modifier.fillMaxWidth().flexGrow(1),
                contentAlignment = if (mobile) Alignment.TopCenter else Alignment.Center
            ) {
            if (mobile) {
                // Mobile: the phone-in-hand shot on top, headline + pill below
                // (adapted from the desktop design — the mobile frame leaves
                // the content zone free)
                Column(
                    Modifier.fillMaxWidth().fillMaxHeight().padding(top = 8.rpx).gap(16.rpx),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    HeroPhoneImage(
                        Modifier
                            .flexGrow(1)
                            .minHeight(0.px)
                            .styleModifier {
                                property("max-height", "44vh")
                                // Nudge up and to the right per the design review
                                property("transform", "translate(7%, -12px)")
                            }
                    )
                    Column(
                        Modifier.fillMaxWidth().gap(16.rpx),
                        horizontalAlignment = Alignment.Start
                    ) {
                        HeroHeadline(fontSize = 42.rpx)
                        HeroBulletPill(fontSize = 16.rpx)
                    }
                }
            } else {
                // Desktop: text on the left; the phone shot is absolutely
                // positioned so it never adds page height (no scroll) and
                // slides under the CTA bar exactly like in the design.
                Box(
                    Modifier.fillMaxWidth().fillMaxHeight().position(Position.Relative),
                    contentAlignment = Alignment.CenterStart
                ) {
                    HeroPhoneImage(
                        Modifier
                            .position(Position.Absolute)
                            .top(0.rpx)
                            .right(0.rpx)
                            // 80px taller than the hero: the photo's cut bottom
                            // edge tucks under the CTA bar (which is z-raised)
                            .styleModifier { property("height", "calc(100% + 80px)") }
                    )
                    Column(
                        Modifier.maxWidth(620.rpx).gap(24.rpx).position(Position.Relative).zIndex(1),
                        horizontalAlignment = Alignment.Start
                    ) {
                        HeroHeadline(fontSize = 72.rpx)
                        HeroBulletPill(fontSize = 22.rpx)
                    }
                }
            }
            }

            // Full-bleed CTA bar, flush with the viewport bottom: 100vw wide
            // (clipped by the layout's overflow) and a negative bottom margin
            // that swallows the page's bottom padding so no background strip
            // peeks out under it.
            Box(
                Modifier
                    // Mobile: extra bottom padding lifts the button off the very
                    // edge of the screen; narrower side padding widens it
                    .padding(
                        top = if (mobile) 10.rpx else 24.rpx,
                        bottom = if (mobile) 22.rpx else 24.rpx,
                        leftRight = if (mobile) 10.rpx else 16.rpx
                    )
                    .margin(top = if (mobile) 8.rpx else 24.rpx, bottom = (-12).rpx)
                    // Above the absolutely-positioned hero photo, which tucks
                    // its cropped bottom edge underneath this bar
                    .position(Position.Relative)
                    .zIndex(1)
                    .styleModifier {
                        property("width", "100vw")
                        property("background-image", "url('/funnel/button-bar-bg.png')")
                        property("background-size", "cover")
                        property("background-position", "center")
                    },
                contentAlignment = Alignment.Center
            ) {
                FunnelCta(
                    text = FunnelCopy.HERO_CTA,
                    hero = true,
                    onClick = {
                        Ga4.event("show_me_how_clicked")
                        FbPixel.trackCustom("QuizStart", "total_questions" to com.routexk.quiz.funnel.QuizDefinition.steps.size)
                        scope.launch {
                            FunnelApi.trackEvent(container.defaultHttpClient, "funnel_start")
                        }
                        ctx.router.navigateTo("/quiz")
                    }
                )
            }
        }
    }
}

@Composable
private fun HeroHeadline(fontSize: CSSSizeValue<CSSUnit.rem>) {
    H1(
        attrs = Modifier
            .margin(0.rpx)
            .fontSize(fontSize)
            .fontWeight(FontWeight.Bold)
            // Design: 66px leading on a 75px face — tighter than default
            .lineHeight(0.95)
            .color(AppColors.Funnel.TextDark)
            .toAttrs()
    ) {
        Text(FunnelCopy.HERO_TITLE_LINE1)
        org.jetbrains.compose.web.dom.Br()
        Span({ style { color(Color(AppColors.Funnel.ProgressFill.toRgbString())) } }) {
            Text(FunnelCopy.HERO_TITLE_LINE2)
        }
    }
}

@Composable
private fun HeroBulletPill(fontSize: CSSSizeValue<CSSUnit.rem> = 18.rpx) {
    Row(
        Modifier
            .padding(topBottom = 14.rpx, leftRight = 20.rpx)
            .borderRadius(100.rpx)
            .border(1.rpx, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0xD1, 0xD2, 0xF0))
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.White)
            .gap(12.rpx)
            .styleModifier { property("box-shadow", "4px 4px 8px rgba(0,0,0,0.05)") },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            Modifier.size(6.rpx).borderRadius(50.percent).flexShrink(0)
                .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
        )
        Column {
            SpanText(
                FunnelCopy.HERO_BULLET_LINE1,
                Modifier
                    .fontSize(fontSize)
                    .lineHeight(1.15)
                    .fontWeight(FontWeight.Bold)
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
            )
            SpanText(
                FunnelCopy.HERO_BULLET_LINE2,
                Modifier
                    .fontSize(fontSize)
                    .lineHeight(1.15)
                    .fontWeight(FontWeight.Bold)
                    .color(com.varabyte.kobweb.compose.ui.graphics.Color.rgb(0x3E, 0x3F, 0x5B))
            )
        }
    }
}

/**
 * The hand-holding-a-phone hero shot (flattened 2x export of the Figma
 * composite, 997x1400, backdrop removed to transparency). Width follows the
 * given height via the intrinsic aspect ratio; the bottom fade hides the
 * photo's cropped wrist where it isn't tucked under the CTA bar.
 */
@Composable
private fun HeroPhoneImage(modifier: Modifier = Modifier) {
    org.jetbrains.compose.web.dom.Img(
        src = "/funnel/hero-phone-hand.png",
        attrs = modifier
            .styleModifier {
                property("object-fit", "contain")
                property("aspect-ratio", "498 / 700")
                property("-webkit-mask-image", "linear-gradient(to bottom, black 86%, transparent 100%)")
                property("mask-image", "linear-gradient(to bottom, black 86%, transparent 100%)")
                property("pointer-events", "none")
            }
            .toAttrs {
                attr("alt", "")
                attr("aria-hidden", "true")
            }
    )
}
